import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { formatLakh, formatDate, timeAgo, formatMeetingType, MEETING_TYPE_OPTIONS } from "@/lib/utils-ros";
import { toast } from "sonner";
import {
  X, Building2, Radio, Zap, Target, ShoppingCart, Ticket as TicketIcon,
  CalendarClock, CheckSquare, Users, Sparkles, MapPin, Factory,
  ArrowRight, Plus, Loader2, Clock, TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PartnerAssignmentPanel } from "./PartnerAssignment";

/**
 * CustomerCard — one persistent drawer that shows the FULL customer history across
 * Universe → Signals → Leads → Opportunities → Orders → Tickets, plus tasks + timeline.
 * Same component wired everywhere so partner/meetings/actions logged in any stage
 * remain visible in every other stage for the same company.
 */

const TIMELINE_ICONS = {
  "signal.captured": Radio, "signal.promoted": ArrowRight,
  "lead.created": Zap, "lead.converted": ArrowRight,
  "opp.created": Target, "opp.won": Target, "opp.updated": Target,
  "order.created": ShoppingCart, "order.cancelled": ShoppingCart,
  "ticket.opened": TicketIcon, "ticket.resolved": TicketIcon, "ticket.closed": TicketIcon,
  "meeting.logged": CalendarClock, "meeting.curated": CalendarClock,
  "task.created": CheckSquare, "task.completed": CheckSquare,
  "partner.assigned": Users, "partner.reassigned": Users,
};

const MEETING_TYPES = MEETING_TYPE_OPTIONS;

function TimelineIcon({ kind }) {
  const Icon = TIMELINE_ICONS[kind] || Sparkles;
  return <Icon size={11} className="text-[var(--copper)]" />;
}

function meetingNotesText(notes) {
  if (!notes) return "";
  if (typeof notes === "string") return notes;
  if (Array.isArray(notes)) {
    return notes.map(n => (typeof n === "string" ? n : n?.content)).filter(Boolean).join("\n\n");
  }
  return "";
}

const STAGE_META = {
  signal: { icon: Radio, label: "Signal", color: "var(--info)" },
  lead: { icon: Zap, label: "Lead", color: "var(--warning)" },
  opportunity: { icon: Target, label: "Opportunity", color: "var(--copper)" },
  order: { icon: ShoppingCart, label: "Order", color: "var(--success)" },
  ticket: { icon: TicketIcon, label: "Ticket", color: "var(--danger)" },
};

export function CustomerCard({ companyId, onClose, defaultTab = "timeline" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState(defaultTab);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get(`/customers/${companyId}/memory`);
      setData(data);
    } catch (e) {
      setData(null);
      const msg = e?.response?.data?.detail || "Failed to load customer";
      setLoadError(typeof msg === "string" ? msg : "Failed to load customer");
      toast.error("Failed to load customer");
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!companyId) return null;

  const content = (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="customer-card">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#F3FAFD] w-full max-w-3xl h-full shadow-2xl flex flex-col animate-slide-in-right md:rounded-l-[30px] border-l border-[#BBD8E7] overflow-hidden">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[var(--copper)]" />
          </div>
        ) : loadError || !data ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <div className="text-[13px] text-[var(--danger)]">{loadError || "Customer data unavailable"}</div>
            <button onClick={load} className="h-9 px-4 rounded-md border border-black/15 text-[12px] hover:border-[var(--saarv-blue)]">
              Retry
            </button>
          </div>
        ) : (
          <>
            <Header company={data.company} partner={data.partner} totals={data.totals} onClose={onClose} />
            <Tabs tab={tab} setTab={setTab} counts={{
              timeline: data.timeline?.length || 0,
              meetings: data.meetings?.length || 0,
              tasks: data.tasks?.filter(t => !["completed","verified","cancelled"].includes(t.status)).length || 0,
              journey: (data.signals?.length || 0) + (data.leads?.length || 0) + (data.opportunities?.length || 0) + (data.orders?.length || 0),
              tickets: data.tickets?.filter(t => t.status !== "closed").length || 0,
              partner: data.company?.partner_id ? 1 : 0,
            }} />
            <div className="flex-1 overflow-y-auto bg-[#F3FAFD]">
              {tab === "timeline" && <TimelinePane events={data.timeline || []} />}
              {tab === "journey" && <JourneyPane data={data} />}
              {tab === "meetings" && <MeetingsPane meetings={data.meetings || []} companyId={companyId} onLog={() => setMeetingOpen(true)} />}
              {tab === "tasks" && <TasksPane tasks={data.tasks || []} companyId={companyId} onAdd={() => setTaskOpen(true)} onReload={load} />}
              {tab === "tickets" && <TicketsPane tickets={data.tickets || []} />}
              {tab === "partner" && <PartnerPane companyId={companyId} />}
            </div>
          </>
        )}
      </div>
      {meetingOpen && <QuickMeetingModal companyId={companyId} onClose={() => { setMeetingOpen(false); load(); }} />}
      {taskOpen && <QuickTaskModal companyId={companyId} onClose={() => { setTaskOpen(false); load(); }} />}
      <style>{`@keyframes slide-in-right { from { transform: translateX(24px); opacity: 0 } to { transform: translateX(0); opacity: 1 } } .animate-slide-in-right { animation: slide-in-right 200ms ease-out; }`}</style>
    </div>
  );
  return createPortal(content, document.body);
}

function Header({ company, partner, totals, onClose }) {
  return (
    <div className="border-b border-[#BBD8E7] p-5 bg-[#E7F5FB]">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-[16px] bg-white border border-[#C9DCE6] shadow-[0_6px_16px_rgba(7,83,154,0.08)] flex items-center justify-center text-[#07539A] text-[13px] font-medium shrink-0">
          {(company?.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#07539A]">Customer 360</div>
          <h2 className="font-display text-xl md:text-2xl tracking-tight text-[#12324A] truncate" data-testid="customer-card-name">
            {company?.name}
          </h2>
          <div className="flex items-center gap-3 text-[11px] text-[#526D7A] mt-1 flex-wrap">
            {company?.industry && <span className="inline-flex items-center gap-1"><Factory size={10} /> {company.industry}</span>}
            {company?.hq_location && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {company.hq_location}</span>}
            {company?.revenue_cr != null && <span className="font-mono">Revenue: {formatLakh(company.revenue_cr)}</span>}
            {partner?.name && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white text-[#07539A] border border-[#BBD8E7]">
                <Users size={10} /> {partner.name} — {partner.firm}
              </span>
            )}
          </div>
        </div>
        <button onClick={onClose} data-testid="customer-card-close"
                className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#526D7A] hover:text-[#07539A] hover:border-[#07539A] shadow-sm">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
        <Stat label="Opps" value={totals?.opportunities || 0} />
        <Stat label="Pipeline" value={formatLakh(totals?.pipeline_cr || 0)} />
        <Stat label="Booked" value={formatLakh(totals?.booked_cr || 0)} highlight />
        <Stat label="Open Tickets" value={totals?.open_tickets || 0} />
        <Stat label="Open Tasks" value={totals?.open_tasks || 0} />
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="min-h-[58px] rounded-[14px] bg-white border border-[#C9DCE6] px-3 py-2 shadow-[0_4px_12px_rgba(7,83,154,0.04)]">
      <div className="text-[9px] uppercase tracking-widest text-[#6F8792]">{label}</div>
      <div className={`font-display text-[15px] tabular-nums ${highlight ? "text-[#B9680E]" : "text-[#12324A]"}`}>{value}</div>
    </div>
  );
}

function Tabs({ tab, setTab, counts }) {
  const items = [
    { k: "timeline", label: "Timeline", count: counts.timeline },
    { k: "journey", label: "Journey", count: counts.journey },
    { k: "meetings", label: "Meetings", count: counts.meetings },
    { k: "tasks", label: "Tasks", count: counts.tasks },
    { k: "tickets", label: "Tickets", count: counts.tickets },
    { k: "partner", label: "Partner", count: counts.partner },
  ];
  return (
    <div className="border-b border-[#C9DCE6] bg-white px-3 py-2 flex gap-1.5 overflow-x-auto shrink-0">
      {items.map(it => {
        const active = tab === it.k;
        return (
          <button key={it.k} onClick={() => setTab(it.k)} data-testid={`cc-tab-${it.k}`}
                  style={active ? { color: "#FFFFFF" } : undefined}
                  className={`px-3.5 h-9 rounded-full text-[12px] whitespace-nowrap flex items-center gap-2 border transition-colors ${
                    active ? "border-[#07539A] bg-[#07539A] !text-white font-medium shadow-sm" : "border-transparent text-[#526D7A] hover:bg-[#E7F5FB] hover:text-[#07539A]"
                  }`}>
            {it.label} <span style={active ? { color: "rgba(255,255,255,0.78)" } : undefined} className={`text-[10px] ${active ? "!text-white/75" : "text-[#78909C]"}`}>{it.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function TimelinePane({ events }) {
  if (events.length === 0) return <Empty msg="No history yet." />;
  return (
    <div className="p-4 space-y-3">
      {events.map((e) => (
        <div key={e.id} className="p-4 flex items-start gap-3 rounded-[18px] bg-white border border-[#C9DCE6] shadow-[0_6px_18px_rgba(7,83,154,0.05)]" data-testid={`cc-event-${e.kind}`}>
          <div className="w-8 h-8 rounded-full bg-[#E7F5FB] flex items-center justify-center shrink-0 mt-0.5">
            <TimelineIcon kind={e.kind} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] uppercase tracking-widest font-mono text-[var(--text-dim)]">{e.kind.replace(".", " › ")}</span>
              <span className="text-[10px] text-[var(--text-dim)]">·</span>
              <span className="text-[10px] text-[var(--text-muted)]">{timeAgo(e.created_at)}</span>
            </div>
            <div className="text-[13px] mt-0.5 text-[#263F4D]">{e.title}</div>
            {e.summary && <div className="text-[12px] text-[#607D8B] mt-1 whitespace-pre-wrap">{e.summary}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function JourneyPane({ data }) {
  const buckets = [
    { key: "signals", label: "Signals", items: data.signals || [], icon: Radio, linkBase: "/signals" },
    { key: "leads", label: "Leads", items: data.leads || [], icon: Zap, linkBase: "/leads" },
    { key: "opportunities", label: "Opportunities", items: data.opportunities || [], icon: Target, linkBase: "/opportunities" },
    { key: "orders", label: "Orders", items: data.orders || [], icon: ShoppingCart, linkBase: "/orders" },
  ];
  return (
    <div className="p-4 space-y-4">
      {buckets.map(b => (
        <div key={b.key} className="border border-[#C9DCE6] rounded-[20px] bg-white overflow-hidden shadow-[0_6px_18px_rgba(7,83,154,0.05)]" data-testid={`cc-journey-${b.key}`}>
          <div className="px-4 py-3 border-b border-[#D7E8F0] flex items-center gap-2 bg-[#E7F5FB]">
            <b.icon size={12} className="text-[#07539A]" />
            <span className="text-[11px] uppercase tracking-widest text-[#07539A] font-semibold">{b.label}</span>
            <span className="font-mono text-[10px] text-[var(--text-dim)]">{b.items.length}</span>
          </div>
          {b.items.length === 0 ? (
            <div className="p-4 text-[12px] text-[var(--text-muted)] text-center">Nothing yet.</div>
          ) : (
            <div className="divide-y divide-black/5">
              {b.items.slice(0, 8).map(it => (
                <Link key={it.id} to={b.key === "opportunities" ? `/opportunities/${it.id}` : b.linkBase}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#F4FAFD]">
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] truncate">{it.name || it.title || it.intent || it.po_number}</div>
                    <div className="text-[10px] text-[var(--text-dim)] mt-0.5">
                      {it.status && <span className="uppercase tracking-wider mr-2">{it.status}</span>}
                      {it.value_cr != null && <span className="font-mono">{formatLakh(it.value_cr)}</span>}
                      {it.gate && <span className="ml-2">Gate {it.gate}</span>}
                      <span className="ml-2">{timeAgo(it.created_at)}</span>
                    </div>
                  </div>
                  <ArrowRight size={12} className="text-[var(--text-dim)]" />
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MeetingsPane({ meetings, onLog }) {
  return (
    <div className="p-4 space-y-3">
      <div className="p-3.5 flex items-center justify-between rounded-[16px] bg-white border border-[#C9DCE6]">
        <span className="text-[11px] uppercase tracking-widest text-[#07539A] font-semibold">All meetings for this customer</span>
        <button onClick={onLog} data-testid="cc-log-meeting"
                className="text-[11px] px-3 py-1.5 rounded-full bg-[#07539A] !text-white hover:bg-[#063F73] flex items-center gap-1">
          <Plus size={11} /> Quick log
        </button>
      </div>
      {meetings.length === 0 ? <Empty msg="No meetings logged for this customer yet." /> : (
        <div className="space-y-3">
          {meetings.map(m => (
            <div key={m.id} className="p-4 rounded-[18px] bg-white border border-[#C9DCE6] shadow-[0_6px_18px_rgba(7,83,154,0.05)]" data-testid={`cc-meeting-${m.id}`}>
              <div className="flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-widest font-mono text-[var(--copper)]">{formatMeetingType(m.meeting_type || "meeting")}</span>
                <span className="text-[10px] text-[var(--text-dim)]">·</span>
                <span className="text-[10px] text-[var(--text-muted)]">{formatDate(m.scheduled_at)}</span>
                {m.opportunity_id && (
                  <Link to={`/opportunities/${m.opportunity_id}`} className="ml-auto text-[10px] text-[var(--saarv-blue)] hover:underline flex items-center gap-1">
                    <Target size={9} /> View opp
                  </Link>
                )}
              </div>
              <div className="text-[13px] mt-0.5">{m.title}</div>
              {meetingNotesText(m.notes) && (
                <div className="text-[12px] text-[var(--text-muted)] mt-1 line-clamp-3 whitespace-pre-wrap">
                  {meetingNotesText(m.notes)}
                </div>
              )}
              {m.ai_summary && (
                <div className="mt-2 p-3 rounded-[14px] bg-[#F1F9FC] border border-[#C9DCE6]">
                  <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#07539A] mb-1">
                    <Sparkles size={9} /> AI Summary
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)] line-clamp-4">{m.ai_summary}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TasksPane({ tasks, onAdd, onReload }) {
  const open = tasks.filter(t => !["completed","verified","cancelled"].includes(t.status));
  const done = tasks.filter(t => ["completed","verified"].includes(t.status));
  return (
    <div className="p-4 space-y-3">
      <div className="p-3.5 flex items-center justify-between rounded-[16px] bg-white border border-[#C9DCE6]">
        <span className="text-[11px] uppercase tracking-widest text-[#07539A] font-semibold">Tasks for this customer</span>
        <button onClick={onAdd} data-testid="cc-add-task"
                className="text-[11px] px-3 py-1.5 rounded-full bg-[#07539A] !text-white hover:bg-[#063F73] flex items-center gap-1">
          <Plus size={11} /> Add task
        </button>
      </div>
      {tasks.length === 0 ? <Empty msg="No tasks yet." /> : (
        <div className="rounded-[20px] bg-white border border-[#C9DCE6] overflow-hidden shadow-[0_6px_18px_rgba(7,83,154,0.05)]">
          <SectionHeader label="Open" n={open.length} />
          <div className="divide-y divide-black/5">
            {open.map(t => <TaskRow key={t.id} t={t} onComplete={async () => { await api.patch(`/tasks/${t.id}`, { status: "completed" }); onReload(); }} />)}
          </div>
          {done.length > 0 && (
            <>
              <SectionHeader label="Completed" n={done.length} />
              <div className="divide-y divide-black/5">
                {done.slice(0, 20).map(t => <TaskRow key={t.id} t={t} done />)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ label, n }) {
  return (
    <div className="px-4 py-2.5 bg-[#E7F5FB] text-[9px] uppercase tracking-widest text-[#07539A] font-semibold">
      {label} · {n}
    </div>
  );
}

function TaskRow({ t, onComplete, done }) {
  return (
    <div className="px-4 py-3 flex items-center gap-3" data-testid={`cc-task-${t.id}`}>
      <button onClick={() => !done && onComplete?.()} disabled={done}
              className={`w-4 h-4 rounded-sm border shrink-0 flex items-center justify-center ${done ? "bg-[var(--success)] border-[var(--success)]" : "border-black/20 hover:border-[var(--copper)]"}`}>
        {done && <CheckSquare size={9} className="text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] ${done ? "line-through text-[var(--text-dim)]" : ""}`}>{t.title}</div>
        <div className="text-[10px] text-[var(--text-dim)] mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="capitalize">{t.source_type?.replace("_", " ") || "manual"}</span>
          {t.priority && <><span>·</span><span className="uppercase tracking-wider">{t.priority}</span></>}
          {t.due_date && <><span>·</span><Clock size={9} className="inline" /> <span>{formatDate(t.due_date)}</span></>}
        </div>
      </div>
    </div>
  );
}

function TicketsPane({ tickets }) {
  if (tickets.length === 0) return <Empty msg="No tickets for this customer." />;
  return (
    <div className="p-4 space-y-3">
      {tickets.map(t => (
        <div key={t.id} className="p-4 rounded-[18px] bg-white border border-[#C9DCE6] shadow-[0_6px_18px_rgba(7,83,154,0.05)]" data-testid={`cc-ticket-${t.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-widest font-mono text-[var(--copper)]">#{t.ticket_no}</span>
            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-black/[0.05]">{t.status}</span>
            {t.priority && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border" style={{ borderColor: t.priority === "critical" ? "var(--danger)" : t.priority === "high" ? "var(--copper)" : "rgba(0,0,0,0.15)" }}>{t.priority}</span>}
            <span className="ml-auto text-[10px] text-[var(--text-muted)]">{timeAgo(t.created_at)}</span>
          </div>
          <div className="text-[13px] mt-1">{t.subject}</div>
          {t.description && <div className="text-[12px] text-[var(--text-muted)] mt-1 line-clamp-2">{t.description}</div>}
        </div>
      ))}
    </div>
  );
}

function PartnerPane({ companyId }) {
  return (
    <div className="p-4">
      <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_6px_18px_rgba(7,83,154,0.05)] [&>[data-testid='partner-assignment']]:border-0">
        <PartnerAssignmentPanel entityType="company" entityId={companyId} />
      </div>
      <p className="mt-3 text-[11px] text-[var(--text-muted)]">
        <TrendingUp size={10} className="inline mr-1 text-[var(--copper)]" />
        Partner assigned here cascades to every downstream Signal / Lead / Opportunity / Order / Ticket for this customer.
      </p>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="m-4 p-10 rounded-[20px] bg-white border border-dashed border-[#BBD8E7] text-center text-[12px] text-[#607D8B]">{msg}</div>;
}

/* -------- Quick Modals -------- */
function QuickMeetingModal({ companyId, onClose }) {
  const [form, setForm] = useState({ title: "", meeting_type: "teams", notes: "" });
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!form.title.trim()) return toast.error("Title required");
    if (!form.notes.trim()) return toast.error("Notes required");
    setSaving(true);
    try {
      await api.post("/meetings/quick-log", {
        title: form.title.trim(),
        entity_type: "company",
        entity_id: companyId,
        meeting_type: form.meeting_type,
        notes: form.notes.trim(),
        scheduled_at: new Date().toISOString(),
      });
      toast.success("Meeting logged");
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save meeting");
    } finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-black/10 rounded-md shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="cc-meeting-modal">
        <div className="p-3 border-b border-black/10 flex items-center gap-2">
          <CalendarClock size={14} className="text-[var(--copper)]" />
          <div className="font-display text-[14px] flex-1">Log Meeting</div>
          <button onClick={onClose}><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <input placeholder="Meeting title" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                 data-testid="cc-meeting-title"
                 className="w-full h-9 px-2 rounded-md bg-white border border-black/15 text-[13px]" />
          <select value={form.meeting_type} onChange={e => setForm({...form, meeting_type: e.target.value})}
                  data-testid="cc-meeting-type"
                  className="w-full h-9 px-2 rounded-md bg-white border border-black/15 text-[13px]">
            {MEETING_TYPES.map(t => <option key={t} value={t}>{formatMeetingType(t)}</option>)}
          </select>
          <textarea rows={4} placeholder="Notes…" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                    data-testid="cc-meeting-notes"
                    className="w-full px-2 py-2 rounded-md bg-white border border-black/15 text-[13px] resize-none" />
          <button onClick={save} disabled={saving} data-testid="cc-meeting-save"
                  className="w-full h-10 rounded-md bg-[var(--copper)] hover:bg-[var(--copper-strong)] text-white text-[13px] font-medium flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function QuickTaskModal({ companyId, onClose }) {
  const [form, setForm] = useState({ title: "", priority: "medium", due_in_days: 2 });
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!form.title.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      await api.post("/tasks", {
        title: form.title, priority: form.priority, task_type: "reminder",
        company_id: companyId,
        due_date: new Date(Date.now() + form.due_in_days * 86400000).toISOString(),
      });
      toast.success("Task added");
      onClose();
    } catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-black/10 rounded-md shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="cc-task-modal">
        <div className="p-3 border-b border-black/10 flex items-center gap-2">
          <CheckSquare size={14} className="text-[var(--copper)]" />
          <div className="font-display text-[14px] flex-1">Add Task</div>
          <button onClick={onClose}><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <input placeholder="What needs doing?" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                 data-testid="cc-task-title"
                 className="w-full h-9 px-2 rounded-md bg-white border border-black/15 text-[13px]" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                    data-testid="cc-task-priority"
                    className="h-9 px-2 rounded-md bg-white border border-black/15 text-[13px]">
              {["low", "medium", "high", "critical"].map(p => <option key={p}>{p}</option>)}
            </select>
            <select value={form.due_in_days} onChange={e => setForm({...form, due_in_days: Number(e.target.value)})}
                    data-testid="cc-task-due"
                    className="h-9 px-2 rounded-md bg-white border border-black/15 text-[13px]">
              <option value={0}>Today</option><option value={1}>Tomorrow</option>
              <option value={3}>In 3 days</option><option value={7}>Next week</option>
            </select>
          </div>
          <button onClick={save} disabled={saving} data-testid="cc-task-save"
                  className="w-full h-10 rounded-md bg-[var(--copper)] hover:bg-[var(--copper-strong)] text-white text-[13px] font-medium flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
          </button>
        </div>
      </div>
    </div>
  );
}
